import React, { forwardRef } from 'react';

// ============================================================================
// Button + IconButton — the button standard (BBM Context for claude/
// buttons.md, Jaylen's picks from the Button Showroom, 2026-09-14).
//
//   variant  primary   flat brand red, darker on hover, no shadow
//            secondary 1px outline on the card color (was `outline`)
//            tertiary  text only, wash on hover (was `ghost`)
//            danger    outlined red that FILLS red on hover — quiet at
//                      rest, the commitment is visible before the click
//            success   flat emerald (Send / Confirm on the crew portal)
//   size     sm 32px · md 40px · lg 48px (the one big button on a phone)
//            radius 8 on all three, matching the form controls
//   loading  label blurs out, spinner fades in, button keeps its width
//   disabled 50% opacity — the button keeps its identity
//
// Press feedback comes from the global `:root button:active` rule in
// index.css. Legacy variant names (`outline`, `ghost`) still resolve.
// ============================================================================

const VARIANTS = {
    primary: 'bg-red-600 hover:bg-red-700 text-white border-transparent',
    secondary: 'bg-white border-slate-300 text-slate-800 hover:bg-slate-50',
    tertiary: 'bg-transparent border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    danger: 'bg-transparent border-red-600 text-red-600 hover:bg-red-600 hover:text-white',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white border-transparent',
};
const LEGACY = { outline: 'secondary', ghost: 'tertiary' };

const SIZES = {
    sm: 'h-8 px-3 text-xs rounded-lg',
    md: 'h-10 px-4 text-sm rounded-lg',
    lg: 'h-12 px-5 text-[15px] rounded-lg',
};

export const Button = forwardRef(function Button(
    {
        variant = 'primary',
        size = 'md',
        loading = false,
        disabled = false,
        leftIcon,
        rightIcon,
        full = false,
        className = '',
        type = 'button',
        children,
        ...rest
    },
    ref
) {
    const v = LEGACY[variant] || variant;
    const variantCls = VARIANTS[v] || VARIANTS.primary;
    const sizeCls = SIZES[size] || SIZES.md;
    const isDisabled = disabled || loading;

    return (
        <button
            ref={ref}
            type={type}
            disabled={isDisabled}
            className={`relative inline-flex items-center justify-center gap-2 border font-semibold whitespace-nowrap select-none transition-colors ${sizeCls} ${variantCls} ${
                full ? 'w-full' : ''
            } focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none ${className}`.trim()}
            {...rest}
        >
            <span
                className="inline-flex items-center gap-2"
                style={{
                    opacity: loading ? 0 : 1,
                    filter: loading ? 'blur(4px)' : 'blur(0px)',
                    transition: 'opacity 180ms var(--ease-out), filter 180ms var(--ease-out)',
                }}
            >
                {leftIcon}
                {children}
                {rightIcon}
            </span>
            <span
                className="absolute inset-0 flex items-center justify-center"
                aria-hidden={!loading}
                style={{ opacity: loading ? 1 : 0, transition: 'opacity 180ms var(--ease-out)', pointerEvents: 'none' }}
            >
                <span className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent" style={{ animation: 'spin 0.7s linear infinite' }} />
            </span>
        </button>
    );
});

// IconButton — icon-only actions on rows and in headers. 36px ghost square
// by default (44 on touch surfaces: size="lg"), a wash on hover, and the
// name on hover via a CSS tooltip (`data-tip`, styled in theme.css) so
// nobody has to guess what a glyph does. `label` is REQUIRED — it is the
// aria-label and the tooltip.
const ICON_SIZES = { sm: 'w-8 h-8', md: 'w-9 h-9', lg: 'w-11 h-11' };
export const IconButton = forwardRef(function IconButton(
    { label, size = 'md', tone = 'neutral', active = false, className = '', type = 'button', children, ...rest },
    ref
) {
    const toneCls = tone === 'danger'
        ? 'text-slate-500 hover:text-red-600 hover:bg-red-50'
        : active
            ? 'bg-slate-900 text-white'
            : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100';
    return (
        <button
            ref={ref}
            type={type}
            aria-label={label}
            data-tip={label}
            className={`bbm-tip relative inline-flex items-center justify-center rounded-lg transition-colors flex-shrink-0 ${ICON_SIZES[size] || ICON_SIZES.md} ${toneCls} focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed ${className}`.trim()}
            {...rest}
        >
            {children}
        </button>
    );
});

export default Button;
